import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMontant, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";

function Field({ label, value, mono }) {
  return (
    <div className="mb-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
      <div className={`text-sm text-black ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

export default function PrintNoteApurement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/notes-apurement/${id}`), api.get("/settings")])
      .then(([n, s]) => { setNote(n.data); setSettings(s.data); })
      .catch(() => navigate("/notes-apurement"));
  }, [id, navigate]);

  if (!note || !settings) {
    return <div className="flex h-screen items-center justify-center bg-neutral-100"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" /></div>;
  }

  const solde = Number(note.montant_debit || 0) - Number(note.montant_apurement || 0);

  return (
    <div className="min-h-screen bg-neutral-200 py-6">
      <div className="mx-auto mb-4 flex max-w-[210mm] items-center justify-between px-4 no-print">
        <Button variant="outline" onClick={() => navigate("/notes-apurement")} data-testid="back-btn"><ArrowLeft className="mr-1.5 h-4 w-4" /> Retour</Button>
        <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800" data-testid="do-print-btn"><Printer className="mr-1.5 h-4 w-4" /> Imprimer / PDF</Button>
      </div>

      <div className="doc-sheet print-area mx-auto max-w-[210mm] bg-white p-10 shadow-lg" style={{ minHeight: "297mm" }} data-testid="print-note-apurement">
        <div className="border-b-2 border-black pb-3 text-center">
          <img src="/logo.png" alt="ONT" className="mx-auto h-24 w-24 object-contain" />
          <h1 className="mt-2 font-heading text-lg font-bold uppercase tracking-wide">
            Note d'Apurement — {note.acte_generateur_nom}
          </h1>
          <p className="font-mono text-sm font-semibold">N° {note.numero}</p>
        </div>

        <div className="mt-4 flex justify-between">
          <div className="rounded border border-black px-3 py-1 text-xs">
            <span className="font-semibold">Note de Perception : </span><span className="font-mono">{note.numero_debit}</span>
          </div>
          <div className="rounded border border-black px-3 py-1 text-xs">
            <span className="font-semibold">Codification : </span><span className="font-mono">{note.code}</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-x-10 gap-y-1">
          <Field label="Note de Taxation" value={note.note_taxation} mono />
          <Field label="Période Perception" value={`${note.mois_debit || ""}  ${note.annee_debit || ""}`} />
          <div className="col-span-2"><Field label="Nom et Raison" value={note.redevable} /></div>
          <Field label="Province" value={note.province} />
          <Field label="Ville" value={note.ville} />
          <Field label="Commune" value={note.commune} />
          <Field label="Quartier" value={note.quartier} />
          <Field label="Acte(s) Générateur(s)" value={note.acte_generateur_nom} />
          <Field label="Date d'apurement" value={`${fmtDate(note.date)}  (${note.mois})`} />
        </div>

        <div className="mt-5 border-y-2 border-black py-3">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Montant Perception</p>
              <p className="font-mono text-lg font-bold text-neutral-800">{fmtMontant(note.montant_debit, note.monnaie)}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Montant Apuré</p>
              <p className="font-mono text-lg font-bold text-emerald-700">{fmtMontant(note.montant_apurement, note.monnaie)}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Solde</p>
              <p className="font-mono text-lg font-bold text-neutral-800">{fmtMontant(solde, note.monnaie)}</p>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Montant apuré en lettres :</span>
            <p className="text-sm font-medium">{note.montant_lettre}</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-10 text-xs">
          <div>
            <p className="font-semibold">Le Redevable</p>
            <div className="mt-10 border-t border-black pt-1 text-center text-[11px] text-neutral-500">Nom & Signature</div>
          </div>
          <div className="text-right">
            <p className="font-semibold">PREPOSÉ / {settings.sigle}</p>
            <div className="mt-10 border-t border-black pt-1 text-center text-[11px] text-neutral-500">Sceau et Signature</div>
          </div>
        </div>
      </div>
    </div>
  );
}
